import { db } from 'config/firebase'
import { calculateProfit } from 'core/utils/inventoryUtils'
import { collection, getDocs, query, where } from 'firebase/firestore'

interface InventoryItem {
  vin: string
  stock_number: string
  make: string
  model: string
  type: string
  size: string
  fuel: string
  miles: number
  year: number
  condition: string
  location: string
  who: string
  bought_from: string
  status: string
  seller_asking_price: string
  link?: string
}

interface ShieldInventoryItem extends InventoryItem {
  email: string
  phone_number: string
}

interface MasterInventoryItem extends InventoryItem {
  show_on_website: boolean
  optic_list_price: string
  profit: string
  sold_date: string
  phone_number: string
}

const convertToCSV = (items: (MasterInventoryItem | ShieldInventoryItem)[], canViewPurchasePrice: boolean, canViewProfit: boolean): string => {
  const isMasterInventory = 'optic_list_price' in items[0]
  const headers = isMasterInventory
    ? [
        'VIN',
        'Stock Number',
        'Make',
        'Model',
        'Type',
        'Size',
        'Fuel',
        'Mileage',
        'Year',
        'Condition',
        'Location',
        'Who',
        'Bought From',
        'Phone',
        'Status',
        'Sale Price',
        'URL',
        'Sold Date',
      ]
    : [
        'VIN',
        'Stock Number',
        'Make',
        'Model',
        'Type',
        'Size',
        'Fuel',
        'Mileage',
        'Year',
        'Condition',
        'Location',
        'Seller Name',
        'Bought From',
        'Email',
        'Phone',
        'Status',
        'Price',
        'URL',
      ]

  // Add "Buy Price" and "Profit" headers only for users with proper permissions
  if (isMasterInventory && canViewPurchasePrice) {
    headers.splice(15, 0, 'Buy Price')
  }
  if (isMasterInventory && canViewProfit) {
    headers.splice(canViewPurchasePrice ? 17 : 16, 0, 'Profit')
  }

  const escapeCommas = (value: string | number | undefined | boolean) => {
    if (value === undefined || value === null) return ''
    if (typeof value === 'string' && value.includes(',')) {
      return `"${value}"`
    }
    return value.toString()
  }

  const rows = items.map(item => {
    let row
    if (isMasterInventory) {
      const masterItem = item as MasterInventoryItem
      row = [
        escapeCommas(masterItem.vin),
        escapeCommas(masterItem.stock_number),
        escapeCommas(masterItem.make),
        escapeCommas(masterItem.model),
        escapeCommas(masterItem.type),
        escapeCommas(masterItem.size),
        escapeCommas(masterItem.fuel),
        escapeCommas(masterItem.miles),
        escapeCommas(masterItem.year),
        escapeCommas(masterItem.condition),
        escapeCommas(masterItem.location),
        escapeCommas(masterItem.who),
        escapeCommas(masterItem.bought_from),
        escapeCommas(masterItem.phone_number),
        escapeCommas(masterItem.status),
        escapeCommas(masterItem.optic_list_price),
        escapeCommas(masterItem.link),
        escapeCommas(masterItem.sold_date),
      ]

      // Add "Buy Price" and "Profit" values only for users with proper permissions
      if (canViewPurchasePrice) {
        row.splice(15, 0, escapeCommas(masterItem.seller_asking_price))
      }
      if (canViewProfit) {
        row.splice(canViewPurchasePrice ? 17 : 16, 0, escapeCommas(masterItem.profit))
      }
    } else {
      const shieldItem = item as ShieldInventoryItem
      row = [
        escapeCommas(shieldItem.vin),
        escapeCommas(shieldItem.stock_number),
        escapeCommas(shieldItem.make),
        escapeCommas(shieldItem.model),
        escapeCommas(shieldItem.type),
        escapeCommas(shieldItem.size),
        escapeCommas(shieldItem.fuel),
        escapeCommas(shieldItem.miles),
        escapeCommas(shieldItem.year),
        escapeCommas(shieldItem.condition),
        escapeCommas(shieldItem.location),
        escapeCommas(shieldItem.who),
        escapeCommas(shieldItem.bought_from),
        escapeCommas(shieldItem.email),
        escapeCommas(shieldItem.phone_number),
        escapeCommas(shieldItem.status),
        escapeCommas(shieldItem.seller_asking_price),
        escapeCommas(shieldItem.link),
      ]
    }

    return row
  })

  const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n')
  return csvContent
}

const downloadCSV = (csvContent: string, fileName: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const downloadShareableInventoryCsv = async (
  collectionName: 'master_inventory' | 'shield_inventory',
  ids: string[],
  canViewPurchasePrice: boolean,
  canViewProfit: boolean,
) => {
  const inventoryCollection = collection(db, collectionName)
  const batchSize = 30
  const inventoryItems: (MasterInventoryItem | ShieldInventoryItem)[] = []

  for (let i = 0; i < ids.length; i += batchSize) {
    const batchIds = ids.slice(i, i + batchSize)
    const inventoryQuery = query(inventoryCollection, where('__name__', 'in', batchIds))
    const querySnapshot = await getDocs(inventoryQuery)

    querySnapshot.forEach(doc => {
      const data = doc.data()
      const baseItem: InventoryItem = {
        vin: data.vin || '',
        who: data.who || '',
        stock_number: data.stock_number || '',
        make: data.make || '',
        model: data.model || '',
        type: data.type || '',
        size: data.size || '',
        fuel: data.fuel || '',
        miles: data.miles || 0,
        year: data.year || '',
        condition: data.condition || '',
        location: data.location || '',
        status: data.status || '',
        seller_asking_price: data.seller_asking_price || '',
        link: data.link || '',
        bought_from: data.bought_from || '',
      }

      const item =
        collectionName === 'master_inventory'
          ? ({
              ...baseItem,
              optic_list_price: data.optic_list_price || '',
              show_on_website: data.show_on_website || '',
              sold_date: data.sold_date || '',
              profit: calculateProfit(String(data.seller_asking_price), String(data.optic_list_price)),
              phone_number: data.phone_number || '',
            } as MasterInventoryItem)
          : ({
              ...baseItem,
              email: data.email || '',
              phone_number: data.phone_number || '',
            } as ShieldInventoryItem)

      inventoryItems.push(item)
    })
  }

  const csvContent = convertToCSV(inventoryItems, canViewPurchasePrice, canViewProfit)
  downloadCSV(csvContent, 'inventory_data.csv')
}
